import { ChangeEvent, FC, MouseEvent, useState } from "react"

import { ReactComponent as IconDocument } from "@common_assets/svg/document-circular.svg"
import { ReactComponent as IconTrash } from "@common_assets/svg/trash-circular.svg"
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import { useMutationImportGroups } from "api/reactQuery/mutations/groups"
import { useMutationImportSchoolGroups } from "api/reactQuery/mutations/schoolGroups"
import { useQueryClassesById } from "api/reactQuery/queries/classes"
import { useQueryGroupImportTemplate } from "api/reactQuery/queries/groups"
import { useQuerySchoolGroupImportTemplate } from "api/reactQuery/queries/schoolGroups"
import { AxiosError, AxiosResponse } from "axios"
import Dialog from "components/common/dialog/dialog"
import IconWrapper from "components/common/icon/iconWrapper"
import { useBlobData } from "hooks/blobData/blobData"
import { useIsSchoolPlan } from "hooks/schoolPlans"
import { useCustomSnackbar } from "hooks/snackbar"
import { useAppSelector } from "hooks/store"
import {
  AddMissingStudentsReview,
  AddMissingStudentsSummary,
  ImportIssuesList,
  isNothingToImport,
  SyncedClassWarning,
} from "pages/groups/components/importGroups"
import { Trans, useTranslation } from "react-i18next"
import { selectUser } from "store/auth/auth.selectors"
import { IGroupImportResult, IGroupScope } from "ts/interfaces/Group"
import {
  GroupImportMode,
  ImportGroupsMode,
  ImportGroupsTarget,
} from "ts/types/Group"
import { getErrorMessage } from "utils/api"
import { getLocalStorageClassname } from "utils/classname/classname"
import { isTeacher } from "utils/roleCheck"

const importModeOptions: {
  value: ImportGroupsMode
  labelKey: string
  descriptionKey: string
}[] = [
  {
    value: "createGroups",
    labelKey: "group.importModeCreateGroups",
    descriptionKey: "group.importModeCreateGroupsDescription",
  },
  {
    value: "addMissingStudents",
    labelKey: "group.importModeAddMissingStudents",
    descriptionKey: "group.importModeAddMissingStudentsDescription",
  },
]

const isImportGroupsMode = (value: string): value is ImportGroupsMode =>
  importModeOptions.some((option) => option.value === value)

interface IProps {
  open: boolean
  target: ImportGroupsTarget
  onClose: () => void
}

const SkippedInfo: FC<{ result: IGroupImportResult; scope: IGroupScope }> = ({
  result,
  scope,
}) => {
  const { t } = useTranslation()

  return (
    <>
      {result.skipped_groups.length > 0 && (
        <Stack gap="4px">
          <Typography variant="body1" fontWeight="500" color="mockup.neutral0">
            {t("group.skippedGroupsTitle")}
          </Typography>
          <Stack gap="4px" color="mockup.error50">
            {result.skipped_groups.map((name) => (
              <Typography variant="body2" key={name}>
                {name}
              </Typography>
            ))}
          </Stack>
        </Stack>
      )}
      {scope === "CLASS" && result.skipped_school_wide_rows > 0 && (
        <Typography variant="body2" color="mockup.neutral10">
          {t("group.skippedSchoolWideRowsCount", {
            count: result.skipped_school_wide_rows,
          })}
        </Typography>
      )}
    </>
  )
}

const ImportResultSummary: FC<{
  result: IGroupImportResult
  scope: IGroupScope
}> = ({ result, scope }) => {
  const { t } = useTranslation()

  return (
    <Stack gap="16px">
      <Typography variant="body1" fontWeight="500" color="mockup.darkBlue">
        {t("group.importedCount", { count: result.imported })}
      </Typography>
      <SkippedInfo result={result} scope={scope} />
    </Stack>
  )
}

const ImportGroupsDialog: FC<IProps> = ({ open, target, onClose }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const userInfo = useAppSelector(selectUser)
  const { scope } = target
  const classId = target.scope === "CLASS" ? target.classId : 0
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [errorText, setErrorText] = useState<string | null>(null)
  const [result, setResult] = useState<IGroupImportResult | null>(null)
  const [hasConfirmedAnyway, setHasConfirmedAnyway] = useState(false)
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false)
  const [mode, setMode] = useState<ImportGroupsMode>("createGroups")
  const isSchoolPlan = useIsSchoolPlan()
  const canAddMissingStudents = scope === "CLASS" && isSchoolPlan
  // Read from the cache filled by `useArchivedClassGuard`; no extra request
  const { data: classData } = useQueryClassesById({
    id: classId,
    options: { enabled: false },
  })
  const isSyncedClass = !!classData?.data.is_synced

  const importOptions = {
    onSuccess: (
      response: AxiosResponse<IGroupImportResult>,
      variables: { onInvalidRows?: GroupImportMode },
    ) => {
      setResult(response.data)
      if (variables.onInvalidRows !== "abort") setHasConfirmedAnyway(true)
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 400) {
        // Back to the upload screen, the only one that shows the inline error
        setResult(null)
        setHasConfirmedAnyway(false)
        setErrorText(getErrorMessage(error))
      } else {
        showSnackbar({ title: t(getErrorMessage(error)), variant: "error" })
      }
    },
  }

  const { mutate: importClassGroups, isLoading: isImportingClassGroups } =
    useMutationImportGroups({ options: importOptions })
  const { mutate: importSchoolGroups, isLoading: isImportingSchoolGroups } =
    useMutationImportSchoolGroups({ options: importOptions })
  const isLoading =
    scope === "CLASS" ? isImportingClassGroups : isImportingSchoolGroups

  const importGroups = (file: File, onInvalidRows?: GroupImportMode) =>
    scope === "CLASS"
      ? importClassGroups({ classId, file, onInvalidRows })
      : importSchoolGroups({ file, onInvalidRows })

  const isAddingMissingStudents =
    canAddMissingStudents && mode === "addMissingStudents"

  const importAddingMissingStudents = (file: File, dryRun: boolean) =>
    importClassGroups({
      classId,
      file,
      onInvalidRows: "skip",
      addMissingStudents: true,
      dryRun,
    })

  const templateOptions = {
    onSuccess: (response: AxiosResponse<Blob>) => {
      const teacher =
        userInfo && isTeacher(userInfo.user) ? userInfo.user : null
      useBlobData({
        response: response.data,
        classname:
          scope === "CLASS"
            ? getLocalStorageClassname()
            : (teacher?.school_name ?? ""),
        exportData: t("group.importTemplateFilename"),
      })
      setIsDownloadingTemplate(false)
    },
    onError: (error: AxiosError) => {
      showSnackbar({ title: t(getErrorMessage(error)), variant: "error" })
      setIsDownloadingTemplate(false)
    },
  }

  useQueryGroupImportTemplate({
    classId,
    options: {
      ...templateOptions,
      enabled: scope === "CLASS" && isDownloadingTemplate,
    },
  })
  useQuerySchoolGroupImportTemplate({
    options: {
      ...templateOptions,
      enabled: scope === "SCHOOL" && isDownloadingTemplate,
    },
  })

  const handleClose = () => {
    setSelectedFile(null)
    setErrorText(null)
    setResult(null)
    setHasConfirmedAnyway(false)
    setMode("createGroups")
    onClose()
  }

  const handleChangeFile = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) return
    setSelectedFile(event.target.files[0])
    setErrorText(null)
  }

  const handleClearFile = (event?: MouseEvent) => {
    event?.preventDefault()
    event?.stopPropagation()
    const input = document.getElementById(
      "import-groups-file-input",
    ) as HTMLInputElement | null
    if (input) input.value = ""
    setSelectedFile(null)
    setErrorText(null)
  }

  const handleDownloadTemplate = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    setIsDownloadingTemplate(true)
  }

  const handleSubmit = () => {
    if (!selectedFile) return
    if (isAddingMissingStudents) {
      importAddingMissingStudents(selectedFile, true)
      return
    }
    importGroups(selectedFile, "abort")
  }

  const handleUploadAnotherFile = () => {
    setResult(null)
    setHasConfirmedAnyway(false)
    handleClearFile()
  }

  const handleImportAnyway = () => {
    if (!selectedFile) return
    importGroups(selectedFile)
  }

  const isReviewingBlockingErrors =
    !!result && result.has_blocking_errors && !hasConfirmedAnyway

  if (isAddingMissingStudents && result) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        titleText={
          result.dry_run
            ? t("group.importPreviewTitle")
            : t("group.importCompleteTitle")
        }
        descriptionText={
          result.dry_run && !isNothingToImport(result)
            ? t("group.importPreviewNothingSaved")
            : undefined
        }
        desktopMaxWidth="534px"
        customActionButtonsSlot={null}
      >
        {result.dry_run ? (
          <AddMissingStudentsReview
            result={result}
            isSyncedClass={isSyncedClass}
            isLoading={isLoading}
            onBack={handleUploadAnotherFile}
            onConfirm={() =>
              selectedFile && importAddingMissingStudents(selectedFile, false)
            }
          />
        ) : (
          <AddMissingStudentsSummary result={result} />
        )}
      </Dialog>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      titleText={
        isReviewingBlockingErrors
          ? t("group.importReviewTitle")
          : t("group.importGroups")
      }
      desktopMaxWidth="534px"
      customActionButtonsSlot={
        result && !isReviewingBlockingErrors ? null : undefined
      }
      actionAcceptText={
        !result
          ? t("group.import")
          : isReviewingBlockingErrors
            ? t("group.importAnyway")
            : t("close")
      }
      isLoading={isLoading}
      actionAcceptButtonProps={{
        disabled: !result ? !selectedFile || isLoading : isLoading,
      }}
      onActionButtonClick={() => {
        if (!result) {
          handleSubmit()
          return
        }
        if (isReviewingBlockingErrors) {
          handleImportAnyway()
          return
        }
        handleClose()
      }}
    >
      {result ? (
        isReviewingBlockingErrors ? (
          <Stack gap="16px">
            <Typography
              variant="body1"
              color="mockup.neutral10"
              fontWeight="500"
            >
              {t("group.importReviewDescription")}
            </Typography>
            <Divider orientation="horizontal" />
            <SkippedInfo result={result} scope={scope} />
            <ImportIssuesList
              titleKey="group.invalidRowsTitle"
              issues={result.invalid_rows}
              scope={scope}
            />
            <ImportIssuesList
              titleKey="group.unmatchedRowsTitle"
              issues={result.unmatched_rows}
              scope={scope}
            />
          </Stack>
        ) : (
          <ImportResultSummary result={result} scope={scope} />
        )
      ) : (
        <Stack>
          {canAddMissingStudents && (
            <RadioGroup
              value={mode}
              onChange={(event) => {
                if (isImportGroupsMode(event.target.value)) {
                  setMode(event.target.value)
                }
              }}
              sx={{ mb: 2, gap: 1 }}
            >
              {importModeOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  sx={{ alignItems: "flex-start" }}
                  label={
                    <Stack pt="2px">
                      <Typography
                        variant="body1"
                        fontWeight="500"
                        color="mockup.darkBlue"
                      >
                        {t(option.labelKey)}
                      </Typography>
                      <Typography variant="body2" color="mockup.neutral10">
                        {t(option.descriptionKey)}
                      </Typography>
                    </Stack>
                  }
                />
              ))}
            </RadioGroup>
          )}
          {isAddingMissingStudents && isSyncedClass && (
            <Box mb={2}>
              <SyncedClassWarning />
            </Box>
          )}
          <Typography variant="body1" color="mockup.neutral10" fontWeight="500">
            <Trans
              i18nKey={
                isAddingMissingStudents
                  ? "group.importGroupsAddMissingStudentsDescription"
                  : "group.importGroupsDescription"
              }
              components={{
                Link: (
                  <Box
                    component="a"
                    href="#"
                    onClick={handleDownloadTemplate}
                  />
                ),
              }}
            />
          </Typography>
          <input
            type="file"
            id="import-groups-file-input"
            style={{ display: "none" }}
            onChange={handleChangeFile}
            accept=".xlsx"
          />
          <label
            htmlFor="import-groups-file-input"
            style={{
              height: "100%",
              marginTop: "16px",
              position: "relative",
              marginBottom: errorText ? "10px" : undefined,
            }}
          >
            {selectedFile && (
              <Box
                component="span"
                role="button"
                tabIndex={0}
                aria-label={t("group.clearSelectedFile")}
                onClick={handleClearFile}
                onMouseDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    e.stopPropagation()
                    handleClearFile()
                  }
                }}
                sx={{
                  cursor: "pointer",
                  border: "none",
                  background: "none",
                  padding: 0,
                  margin: 0,
                }}
              >
                <IconTrash
                  style={{
                    color: theme.palette.primary.main,
                    position: "absolute",
                    top: "23px",
                    right: "24px",
                    zIndex: 2,
                    cursor: "pointer",
                  }}
                />
              </Box>
            )}
            <Button
              variant="outlined"
              component="span"
              sx={(theme) => ({
                width: "100%",
                minHeight: "200px",
                display: "flex",
                border: `2px solid ${
                  errorText
                    ? theme.palette.mockup.error50
                    : theme.palette.mockup.primary95
                }`,
                borderRadius: "16px",
                justifyContent: "center",
                textTransform: "none",
              })}
            >
              <Stack
                margin="auto"
                maxWidth="100%"
                alignItems="center"
                px="32px"
              >
                {selectedFile ? (
                  <Typography
                    variant="body1"
                    color="mockup.darkBlue"
                    sx={{ overflowWrap: "anywhere" }}
                  >
                    {selectedFile.name}
                  </Typography>
                ) : (
                  <>
                    <IconWrapper>
                      <IconDocument />
                    </IconWrapper>
                    <Typography
                      variant="body1"
                      color={errorText ? "mockup.error50" : "mockup.darkBlue"}
                      mt={2}
                    >
                      {t("group.selectFileToUpload")}
                    </Typography>
                  </>
                )}
              </Stack>
            </Button>
          </label>
          {errorText && (
            <Stack color="mockup.error50">
              <Typography sx={{ mb: 1 }}>{t("group.importFailed")}</Typography>
              <Typography fontWeight="500" variant="body1">
                {errorText}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
    </Dialog>
  )
}

export default ImportGroupsDialog
